import type { ForecastingRow } from '@/api/forecasting';

/** Coerce API / BigQuery row shapes into the dashboard field names. */
export function normalizeForecastingRow(raw: Record<string, unknown>): ForecastingRow {
  const pick = (...keys: string[]): string => {
    for (const key of keys) {
      const v = raw[key];
      if (v != null && v !== '') return String(v);
    }
    return '';
  };

  const pickNum = (...keys: string[]): string => {
    for (const key of keys) {
      const v = raw[key];
      if (v != null && v !== '') return String(v);
    }
    return '0';
  };

  const pickOptional = (...keys: string[]): string | undefined => {
    for (const key of keys) {
      const v = raw[key];
      if (v != null && v !== '') return String(v);
    }
    return undefined;
  };

  return {
    Campaign_ID: pick('Campaign_ID', 'campaign_id', 'CampaignID'),
    Line_Item_ID: pick('Line_Item_ID', 'line_item_id', 'LineItemID'),
    Booking_Date: pick('Booking_Date', 'booking_date', 'BookingDate') || null,
    Status: pick('Status', 'status'),
    Buying_Point: pick('Buying_Point', 'buying_point', 'BuyingPoint'),
    Advertiser: pick('Advertiser', 'advertiser'),
    Campaign: pick('Campaign', 'campaign'),
    Channel: pick('Channel', 'channel'),
    Platform: pick('Platform', 'platform'),
    Month: pick('Month', 'month'),
    Year: pick('Year', 'year'),
    Start_Date: pick('Start_Date', 'start_date', 'StartDate'),
    End_Date: pick('End_Date', 'end_date', 'EndDate'),
    Office_Net_Revenue: pickNum(
      'Office_Net_Revenue',
      'office_net_revenue',
      'Revenue',
      'revenue',
      'OfficeNetRevenue'
    ),
    Skyrise_Powered: pick('Skyrise_Powered', 'skyrise_powered'),
    Recognised_Revenue__: pickNum('Recognised_Revenue__', 'recognised_revenue', 'Recognised_Revenue'),
    Margin__: pickNum('Margin__', 'margin', 'Margin'),
    Net_Rev__Rec_: pickNum(
      'Net_Rev__Rec_',
      'net_rev_rec',
      'Net_Rev_Rec',
      'Net Rev (Rec)',
      'Net_Rev__Rec__'
    ),
    Revenue_Weighting: pickOptional(
      'Revenue_Weighting',
      'revenue_weighting',
      'Revenue Weighting',
      'Revenue_Weighting_1_or_0'
    ),
    Platform_Spend: pickNum('Platform_Spend', 'platform_spend'),
    Hard_Costs: pickNum('Hard_Costs', 'hard_costs'),
    Telco_Costs: pickNum('Telco_Costs', 'telco_costs'),
    Rebate: pickNum('Rebate', 'rebate'),
    Referral: pickNum('Referral', 'referral'),
    Gross_Margin: pickNum('Gross_Margin', 'gross_margin'),
    Gross_Profit: pickNum('Gross_Profit', 'gross_profit', 'GP', 'gp'),
    Quarter: pick('Quarter', 'quarter'),
    Customer_Type: pick('Customer_Type', 'customer_type', 'Client_Type', 'client_type'),
    Month__Date_: pick('Month__Date_', 'month_date', 'Month_Date', 'Month (Date)'),
    Sales: pick('Sales', 'sales'),
    AM: pick('AM', 'am'),
    Forecast_Stage: pick('Forecast_Stage', 'forecast_stage', 'ForecastStage', 'Forecast Stage'),
    Simplified_Stages: pick(
      'Simplified_Stages',
      'simplified_stages',
      'Simplified Stages',
      'Simplified_Stage',
      'Simplified Stage'
    ),
    Skyrise_Type: pick('Skyrise_Type', 'skyrise_type'),
    Skyrise_Powered__: pick('Skyrise_Powered__', 'skyrise_powered__'),
    Source: pick('Source', 'source'),
    FY: pick('FY', 'fy', 'Fy'),
    New_Existing: pick('New_Existing', 'new_existing'),
    Territory: pick('Territory', 'territory')
  };
}

export function normalizeForecastingResponse(response: unknown): ForecastingRow[] {
  if (Array.isArray(response)) {
    return response.map((row) => normalizeForecastingRow(row as Record<string, unknown>));
  }

  if (response && typeof response === 'object') {
    const obj = response as Record<string, unknown>;
    const nested = obj.data ?? obj.rows ?? obj.results ?? obj.items;
    if (Array.isArray(nested)) {
      return nested.map((row) => normalizeForecastingRow(row as Record<string, unknown>));
    }
  }

  return [];
}
